import { APCAcontrast, sRGBtoY } from 'apca-w3';
import { color, hsl, rgb, RGBColor } from 'd3-color';
import { scaleLinear } from 'd3-scale';
import _ from 'lodash';
import seedrandom from 'seedrandom';
import { TwTheme } from '../../Tw';
import {
  booleanColors,
  booleanComplianceColors,
  defaultQualitativeColors,
  dimensionColors,
  policyColors,
  tagsColors,
  waypointEndColors,
  waypointStarColors,
} from './qualitativePalettes';
import {
  categoryHotToColdColor,
  defaultSequentialColors,
  elevationSortColors,
  elevationSortColorsReverse,
  heatmapAnalytics,
  signalColors,
  stackingBottomColors,
  stackingTopColors,
  trendAscending,
  trendDescending,
  trendNoChange,
  trendOutlier,
} from './sequentialPalettes';

const defaultTextColor = 'white';
const defaultQualitativePaletteKey = 'qualitative-default';
export const qualitativePaletteIds = [
  'dimension',
  'policy',
  'waypointStart',
  'waypointEnd',
  'boolean',
  'booleanCompliance',
  'tags',
] as const;

export const sequentialPaletteIds = [
  'elevation-sort',
  'elevation-sort-reverse',
  'signal-strength',
  'stacking-top',
  'stacking-bottom',
  'heatmap-analytics',
  'velocity-analytics',
  'trendAscending',
  'trendDescending',
  'trendNoChange',
  'trendOutlier',
] as const;

export type QualitativePaletteId = (typeof qualitativePaletteIds)[number];
export type SequentialPaletteId = (typeof sequentialPaletteIds)[number];

const qualitativePaletteMap: Record<QualitativePaletteId, string[]> = {
  dimension: dimensionColors,
  policy: policyColors,
  waypointStart: waypointStarColors,
  waypointEnd: waypointEndColors,
  boolean: booleanColors,
  booleanCompliance: booleanComplianceColors,
  tags: tagsColors,
};

const sequentialPaletteMap: Record<SequentialPaletteId, string[]> = {
  'elevation-sort': elevationSortColors,
  'elevation-sort-reverse': elevationSortColorsReverse,
  'signal-strength': signalColors,
  'stacking-top': stackingTopColors,
  'stacking-bottom': stackingBottomColors,
  'heatmap-analytics': heatmapAnalytics,
  'velocity-analytics': categoryHotToColdColor,
  trendAscending,
  trendDescending,
  trendNoChange,
  trendOutlier,
};

const qualitativePaletteIndexes: Partial<
  Record<QualitativePaletteId | typeof defaultQualitativePaletteKey, number>
> = {};

/**
 * creates gradient by provided paletteId
 * return function that provide color value by numeric value in range [0, 100]
 */
const getGradientColor = _.memoize((paletteId: SequentialPaletteId) => {
  const colors = sequentialPaletteMap[paletteId] ?? defaultSequentialColors;
  const base = _.size(colors);
  const domain = _.range(base).map(i => Math.floor((i * 100) / (base - 1)));
  return scaleLinear()
    .domain(domain)
    .range(colors as any);
});

export function getSequentialColor(
  v: number,
  paletteId: SequentialPaletteId,
): [string, string] {
  const color = getGradientColor(paletteId)(v)?.toString();
  return [color, defaultTextColor];
}

// Convert HEX to RGBA
function hexToRGBA(hex, opacity) {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Convert RGB to RGBA
function rgbToRGBA(rgb, opacity) {
  return rgb.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
}

// Convert HSL to HSLA
function hslToHSLA(hsl, opacity) {
  return hsl.replace('hsl', 'hsla').replace(')', `, ${opacity})`);
}

// Identify color format and convert to RGBA or HSLA
export function colorWithOpacity(color, opacity) {
  if (color.startsWith('#')) {
    return hexToRGBA(color, opacity);
  } else if (color.startsWith('rgb')) {
    return rgbToRGBA(color, opacity);
  } else if (color.startsWith('hsl')) {
    return hslToHSLA(color, opacity);
  } else {
    return color; // Unknown or unsupported format
  }
}

// Helper function to calculate luminance
function getLuminance(colorValue: string): number {
  // Assuming color() parses to {r, g, b} object with 0-255 values
  const col = color(colorValue); // This needs to be defined or replaced with correct parsing logic
  if (!col) return 0;

  const rgbColor: RGBColor = rgb(col); // Ensure this function correctly normalizes RGB values
  const [r, g, b] = [rgbColor.r, rgbColor.g, rgbColor.b].map(channel => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // console.log(`Luminance of ${colorValue}: ${luminance}`); // Debugging line
  return luminance;
}

// Helper function to calculate contrast ratio
function getContrastRatio(colorA: string, colorB: string): number {
  const luminanceA = getLuminance(colorA) + 0.05;
  const luminanceB = getLuminance(colorB) + 0.05;

  return luminanceA > luminanceB
    ? luminanceA / luminanceB
    : luminanceB / luminanceA;
}

// Function to determine best contrast color (black or white)
export function getBestContrastColor2(colorValue: string): string {
  const textDefaultColors = TwTheme.extend.colors.text;

  const whiteContrast = getContrastRatio(colorValue, textDefaultColors.light);
  const blackContrast = getContrastRatio(colorValue, textDefaultColors.dark);

  console.log(
    `${colorValue} Contrast with white: ${whiteContrast}, meets WCAG: ${whiteContrast >= 4.5}`,
  );
  console.log(
    `${colorValue} Contrast with black: ${blackContrast}, meets WCAG: ${blackContrast >= 4.5}`,
  );

  return whiteContrast > blackContrast
    ? textDefaultColors.light
    : textDefaultColors.dark;
}

// Function to convert any color input to RGB and then calculate APCA contrast score
function calculateApcaContrast(color1: string, color2: string): number {
  const color1RGB = rgb(color(color1)).formatHex();
  const color2RGB = rgb(color(color2)).formatHex();
  const luminance1 = sRGBtoY([
    parseInt(color1RGB.slice(1, 3), 16),
    parseInt(color1RGB.slice(3, 5), 16),
    parseInt(color1RGB.slice(5, 7), 16),
  ]);
  const luminance2 = sRGBtoY([
    parseInt(color2RGB.slice(1, 3), 16),
    parseInt(color2RGB.slice(3, 5), 16),
    parseInt(color2RGB.slice(5, 7), 16),
  ]);
  const contrast = APCAcontrast(luminance1, luminance2);
  // console.log(
  //   `APCA Contrast between ${color1RGB} and ${color2RGB}: ${contrast}`,
  // );
  return contrast;
}

export function getBestContrastColor(colorValue: string): string {
  const textDefaultColors = TwTheme.extend.colors.text;

  // Assuming veryLight and veryDark are adjusted to meet most cases
  const veryLightLuminance = 0.85; // Example luminance value
  const veryDarkLuminance = 0.15; // Example luminance value

  const inputHSL = hsl(color(colorValue));

  // Generate very light and very dark versions of the input color
  const veryLightColor = hsl(
    inputHSL.h,
    inputHSL.s,
    veryLightLuminance,
  ).formatRgb();
  const veryDarkColor = hsl(
    inputHSL.h,
    inputHSL.s,
    veryDarkLuminance,
  ).formatRgb();

  // console.log(
  //   `Generated Colors: Very Light - ${veryLightColor}, Very Dark - ${veryDarkColor}`,
  // );

  // Calculate APCA contrast scores for the generated colors
  const veryLightContrast = calculateApcaContrast(colorValue, veryLightColor);
  const veryDarkContrast = calculateApcaContrast(colorValue, veryDarkColor);

  let chosenColor = '';
  let meetsAPCA = false;
  let isFallback = false;

  // APCA thresholds: Typically, a contrast value of 60 or more is needed for text readability.
  const apcaThreshold = 60;

  // Choose the color that meets APCA contrast minimums, if any
  if (
    Math.abs(veryLightContrast) >= apcaThreshold ||
    Math.abs(veryDarkContrast) >= apcaThreshold
  ) {
    chosenColor =
      Math.abs(veryLightContrast) > Math.abs(veryDarkContrast)
        ? veryLightColor
        : veryDarkColor;
    meetsAPCA = true;
  } else {
    // Fallback strategy: Try different predefined high-contrast colors
    const fallbackColors = [textDefaultColors.light, textDefaultColors.dark];
    let bestContrast = 0;

    fallbackColors.forEach(fallbackColor => {
      const contrast = Math.abs(
        calculateApcaContrast(colorValue, fallbackColor),
      );
      // console.log(`Fallback Color: ${fallbackColor}, Contrast: ${contrast}`);
      if (contrast > bestContrast) {
        bestContrast = contrast;
        chosenColor = fallbackColor;
      }
    });

    meetsAPCA = bestContrast >= apcaThreshold;
    isFallback = true;
  }

  // Ensure chosenColor is defined
  if (!chosenColor) {
    chosenColor = textDefaultColors.light; // Default to a known high-contrast color
    isFallback = true;
    meetsAPCA = calculateApcaContrast(colorValue, chosenColor) >= apcaThreshold;
  }

  // Visual confirmation in the console
  // console.log(
  //   `%c Original ${colorValue} %c Chosen ${chosenColor} (APCA: ${meetsAPCA ? 'Yes' : 'No'}) ${isFallback ? ' (Fallback)' : ''} `,
  //   `background: ${colorValue}; color: ${chosenColor}; padding: 2px 5px; border-radius: 2px;`,
  //   `${meetsAPCA ? textDefaultColors.dark : textDefaultColors.light}; padding: 2px 5px; border-radius: 2px;`,
  // );

  return chosenColor;
}

// Color assignment tracking for debugging
interface ColorAssignment {
  key: string;
  paletteId: QualitativePaletteId;
  colorIndex: number;
  color: string;
  timestamp: number;
}

const colorAssignments: ColorAssignment[] = [];

// Debugging utilities
export const colorDebugUtils = {
  // Get all color assignments for analysis
  getColorAssignments: () => colorAssignments,

  // Clear assignment history
  clearAssignments: () => {
    colorAssignments.length = 0;
  },

  // Find duplicate color assignments
  findDuplicateColors: (paletteId?: QualitativePaletteId) => {
    const filtered = paletteId
      ? colorAssignments.filter(a => a.paletteId === paletteId)
      : colorAssignments;

    const colorMap = new Map<string, ColorAssignment[]>();
    filtered.forEach(assignment => {
      const key = assignment.color;
      if (!colorMap.has(key)) {
        colorMap.set(key, []);
      }
      colorMap.get(key)!.push(assignment);
    });

    return Array.from(colorMap.entries())
      .filter(([, assignments]) => assignments.length > 1)
      .map(([color, assignments]) => ({ color, assignments }));
  },

  // Test seedrandom behavior with different keys
  testSeedRandomDistribution: (
    keys: string[],
    paletteId: QualitativePaletteId,
  ) => {
    const colors = qualitativePaletteMap[paletteId] ?? defaultQualitativeColors;
    const colorsCount = colors.length;

    return keys.map(key => {
      const rng = seedrandom(key);
      const randomValue = rng();
      const colorIndex = Math.floor(randomValue * colorsCount);
      return {
        key,
        randomValue,
        colorIndex,
        color: colors[colorIndex],
      };
    });
  },

  // Analyze palette uniqueness
  analyzePaletteUniqueness: (paletteId: QualitativePaletteId) => {
    const colors = qualitativePaletteMap[paletteId] ?? defaultQualitativeColors;
    const uniqueColors = new Set(colors);
    const duplicates = colors.filter(
      (color, index) => colors.indexOf(color) !== index,
    );

    return {
      totalColors: colors.length,
      uniqueColors: uniqueColors.size,
      duplicateColors: [...new Set(duplicates)],
      duplicateIndices: duplicates.map(color => colors.indexOf(color)),
    };
  },

  // Test color similarity
  testColorSimilarity: (color1: string, color2: string) => {
    const c1 = color(color1);
    const c2 = color(color2);

    if (!c1 || !c2) return null;

    const rgb1 = rgb(c1);
    const rgb2 = rgb(c2);
    const hsl1 = hsl(c1);
    const hsl2 = hsl(c2);

    // Calculate RGB distance
    const rgbDistance = Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) +
        Math.pow(rgb1.g - rgb2.g, 2) +
        Math.pow(rgb1.b - rgb2.b, 2),
    );

    // Calculate HSL distance (weighted)
    const hueDistance = Math.min(
      Math.abs(hsl1.h - hsl2.h),
      360 - Math.abs(hsl1.h - hsl2.h),
    );
    const satDistance = Math.abs(hsl1.s - hsl2.s);
    const lightDistance = Math.abs(hsl1.l - hsl2.l);

    return {
      rgbDistance,
      hueDistance,
      saturationDistance: satDistance,
      lightnessDistance: lightDistance,
      isSimilar:
        rgbDistance < 50 ||
        (hueDistance < 30 && satDistance < 0.3 && lightDistance < 0.3),
    };
  },

  // Comprehensive debug analysis
  runColorAnalysis: () => {
    console.group('🎨 Color Assignment Analysis');

    // Analyze each palette
    qualitativePaletteIds.forEach(paletteId => {
      console.group(`📊 Palette: ${paletteId}`);

      const uniquenessAnalysis =
        colorDebugUtils.analyzePaletteUniqueness(paletteId);
      console.log('Palette Analysis:', uniquenessAnalysis);

      if (uniquenessAnalysis.duplicateColors.length > 0) {
        console.warn(
          `⚠️  Found ${uniquenessAnalysis.duplicateColors.length} duplicate colors:`,
          uniquenessAnalysis.duplicateColors,
        );
      }

      const currentAssignments = colorAssignments.filter(
        a => a.paletteId === paletteId,
      );
      if (currentAssignments.length > 0) {
        const duplicateAssignments =
          colorDebugUtils.findDuplicateColors(paletteId);
        if (duplicateAssignments.length > 0) {
          console.warn(
            `🔄 Found ${duplicateAssignments.length} color conflicts:`,
            duplicateAssignments,
          );
        }
      }

      console.groupEnd();
    });

    // Test with sample keys
    const testKeys = [
      'warehouse-1',
      'warehouse-2',
      'warehouse-3',
      'policy-A',
      'policy-B',
      'policy-C',
    ];
    console.group('🧪 Test Key Distribution');

    qualitativePaletteIds.slice(0, 3).forEach(paletteId => {
      const distribution = colorDebugUtils.testSeedRandomDistribution(
        testKeys,
        paletteId,
      );
      console.log(`${paletteId}:`, distribution);
    });

    console.groupEnd();
    console.groupEnd();

    return {
      paletteAnalysis: qualitativePaletteIds.map(id => ({
        paletteId: id,
        ...colorDebugUtils.analyzePaletteUniqueness(id),
      })),
      assignments: colorAssignments,
      duplicates: colorDebugUtils.findDuplicateColors(),
    };
  },
};

// Modified getQualitativeColor function to use best contrast color and track assignments
export const getQualitativeColorLegacy: (
  key: string,
  paletteId: QualitativePaletteId,
) => [string, string] = _.memoize(
  (key: string, paletteId: QualitativePaletteId) => {
    const rng = seedrandom(_.join(key, '-'))();

    const pid = paletteId ?? defaultQualitativePaletteKey;
    const colors: string[] =
      qualitativePaletteMap[pid] ?? defaultQualitativeColors;
    const colorsCount = _.size(colors);
    const colorIndex = Math.floor(rng * colorsCount);

    console.log(
      `### ColorGen log: key: ${key}, rng: ${rng}, colorIndex : ${colorIndex}`,
    );
    // const colorIndex = qualitativePaletteIndexes[pid] ?? 0;
    // qualitativePaletteIndexes[pid] = colorIndex + 1;

    const colorBase = colors[colorIndex];
    if (_.isNull(color)) {
      console.log('[Color Helper] Color is nil', colorsCount, colorIndex);
    }
    // Text Color
    const textColor = getBestContrastColor(colorBase); // Use best contrast color
    return [colorBase, textColor];
  },
  (...args) => _.join(args, '-'),
);

// Improved color assignment function that removes duplicates and provides better distribution
const getUniqueColorsFromPalette = _.memoize(
  (paletteId: QualitativePaletteId) => {
    const colors = qualitativePaletteMap[paletteId] ?? defaultQualitativeColors;
    return [...new Set(colors)]; // Remove duplicates
  },
);

export const getQualitativeColorImproved: (
  key: string,
  paletteId: QualitativePaletteId,
) => [string, string] = _.memoize(
  (key: string, paletteId: QualitativePaletteId) => {
    const pid = paletteId ?? defaultQualitativePaletteKey;
    const uniqueColors = getUniqueColorsFromPalette(
      pid as QualitativePaletteId,
    );
    const colorsCount = uniqueColors.length;

    // Use a more sophisticated hash function for better distribution
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Use multiple hash iterations to avoid clustering
    const hash1 = Math.abs(hash) % colorsCount;
    const hash2 = Math.abs(hash * 31 + 17) % colorsCount;
    const hash3 = Math.abs(hash * 37 + 23) % colorsCount;

    // Select the hash that provides the best separation from recently used colors
    const recentAssignments = colorAssignments
      .filter(a => a.paletteId === pid && Date.now() - a.timestamp < 60000) // Last minute
      .map(a => a.colorIndex);

    const candidates = [hash1, hash2, hash3];
    let bestIndex = hash1;
    let maxDistance = 0;

    candidates.forEach(candidate => {
      const minDistance =
        recentAssignments.length > 0
          ? Math.min(
              ...recentAssignments.map(recent => Math.abs(candidate - recent)),
            )
          : colorsCount; // If no recent assignments, any index is good

      if (minDistance > maxDistance) {
        maxDistance = minDistance;
        bestIndex = candidate;
      }
    });

    const colorIndex = bestIndex;
    const colorBase = uniqueColors[colorIndex];

    // Track assignment for debugging
    const assignment: ColorAssignment = {
      key,
      paletteId: pid as QualitativePaletteId,
      colorIndex,
      color: colorBase,
      timestamp: Date.now(),
    };
    colorAssignments.push(assignment);

    // console.log('getQualitativeColorImproved', {
    //   key: key,
    //   paletteId: paletteId,
    //   hash1,
    //   hash2,
    //   hash3,
    //   selectedIndex: colorIndex,
    //   color: colorBase,
    //   totalUniqueColors: colorsCount,
    //   recentCount: recentAssignments.length,
    // });

    const textColor = getBestContrastColor(colorBase);
    return [colorBase, textColor];
  },
  (...args) => _.join(args, '-'),
);

// Main function - now uses the improved version that removes duplicates
export const getQualitativeColor: (
  key: string,
  paletteId: QualitativePaletteId,
) => [string, string] = getQualitativeColorImproved;

// HSL-based color assignment for even better distribution
export const getQualitativeColorHSL: (
  key: string,
  paletteId: QualitativePaletteId,
) => [string, string] = _.memoize(
  (key: string, paletteId: QualitativePaletteId) => {
    const pid = paletteId ?? defaultQualitativePaletteKey;

    // Generate hash from key
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    // Use golden ratio for better distribution
    const goldenRatio = 0.618033988749895;
    const hue = (Math.abs(hash) * goldenRatio) % 360;

    // Use predefined good saturation and lightness values
    const saturations = [0.7, 0.8, 0.9, 0.6];
    const lightnesses = [0.5, 0.4, 0.6, 0.45];

    const satIndex = Math.abs(hash >> 8) % saturations.length;
    const lightIndex = Math.abs(hash >> 16) % lightnesses.length;

    const saturation = saturations[satIndex];
    const lightness = lightnesses[lightIndex];

    const colorBase = hsl(hue, saturation, lightness).formatHex();

    // Track assignment for debugging
    const assignment: ColorAssignment = {
      key,
      paletteId: pid as QualitativePaletteId,
      colorIndex: Math.floor(hue),
      color: colorBase,
      timestamp: Date.now(),
    };
    colorAssignments.push(assignment);

    console.log('getQualitativeColorHSL', {
      key: key,
      paletteId: paletteId,
      hash,
      hue: hue.toFixed(1),
      saturation: saturation.toFixed(2),
      lightness: lightness.toFixed(2),
      color: colorBase,
    });

    const textColor = getBestContrastColor(colorBase);
    return [colorBase, textColor];
  },
  (...args) => _.join(args, '-'),
);

export function getColorDarker(colorValue: string, darkness = 1): string {
  const fallbackColor = '#000000'; // Define your fallback color here
  try {
    // Ensure colorValue is defined and not null by using a fallback color if necessary
    const safeColorValue = colorValue || fallbackColor;
    const resultColor = color(safeColorValue)?.darker(darkness).toString();
    return resultColor ? resultColor : fallbackColor; // Use resultColor if it's valid, else fallbackColor
  } catch (ex) {
    console.error(
      `[Color Helper] Can't get darker version of color: ${colorValue}`,
      ex,
    );
    return fallbackColor; // Return fallback color in case of error
  }
}

export function applyColorOpacity(colorValue: string, opacity = 0.8): string {
  const fallbackColor = '#000000'; // Define your fallback color here

  try {
    const safeColorValue = colorValue || fallbackColor;
    const resultColor = color(safeColorValue).copy({ opacity }).toString();
    return resultColor ? resultColor : fallbackColor; // Use resultColor if it's valid, else fallbackColor
  } catch (ex) {
    console.error(
      `[Color Helper] Can't change opacity of color: ${colorValue}`,
      ex,
    );
    return fallbackColor; // Return fallback color in case of error
  }
}

type ColorManipulationOptions = {
  opacity?: number;
  darkness?: number;
  lightness?: number;
  saturation?: number;
  overrideColor?: string;
  desaturate?: boolean;
};

export function manipulateColor(
  colorValue: string,
  options: ColorManipulationOptions,
): string {
  try {
    let initialColor = options.overrideColor
      ? color(options.overrideColor)
      : color(colorValue);
    let hslColor = hsl(initialColor); // Convert to HSL at the beginning

    if (options.desaturate) {
      hslColor.s = 0;
    }

    if (options.opacity !== undefined) {
      hslColor.opacity = options.opacity;
    }

    if (options.darkness !== undefined) {
      hslColor.l = hslColor.l * (1 / (1 + options.darkness));
    }

    if (options.lightness !== undefined) {
      hslColor.l += options.lightness;
    }

    if (options.saturation !== undefined) {
      hslColor.s = options.saturation;
    }

    return hslColor + ''; // Convert HSL back to string format, which will be RGB
  } catch (ex) {
    console.error(`Can't manipulate color: ${colorValue}`, ex);
    return colorValue;
  }
}
